import React from 'react';
import { t } from '@lingui/macro';

import type { BrandCountryServices } from '@rover/shared/js/hooks/useLocalizedBrandCountryServices';
import {
  getServiceLinkDisplayName,
  NAVBAR_SERVICE_OPTIONS,
} from '@rover/shared/js/ssr/BaseTemplate/NavBar/constants';
import type { RoverTheme } from '@rover/types/src/RoverTheme';
import { ICON_FOR_SERVICE_LANDING_ENUM, ServiceLandingEnum } from '@rover/types/src/ServiceLanding';

export type ServiceDescription = {
  IconClass?: React.ComponentType<{ fill?: string }>;
  title: string;
  content: string;
  color: string;
  hasBorder: boolean;
  href?: string;
  serviceLandingType?: ServiceLandingEnum;
};

export const getServiceDescriptionFromLandingEnum = (
  serviceLandingType: ServiceLandingEnum,
  services: BrandCountryServices | null,
  colors: RoverTheme['colors'],
  {
    shouldShowTrainingEntrypoints = false,
  }: {
    shouldShowTrainingEntrypoints?: boolean;
    countryConfigurationHasDogTraining?: boolean;
  } = {}
): ServiceDescription | null => {
  // Handle special case for dog training
  const isTraining = serviceLandingType === ServiceLandingEnum.DOG_TRAINING;
  if (isTraining && !shouldShowTrainingEntrypoints) {
    return null;
  }

  const href = services?.[serviceLandingType]?.url;

  // Homepage descriptions
  const getServiceContent = (landingEnum: ServiceLandingEnum): string => {
    switch (landingEnum) {
      case ServiceLandingEnum.DOG_BOARDING:
        return t`Your pets stay overnight in your sitter’s home. They’ll be treated like part of the family in a familiar environment.`;
      case ServiceLandingEnum.HOUSE_SITTING:
        return t`Your sitter takes care of your pets and your house. Your pets will get all the attention they need from the comfort of home.`;
      case ServiceLandingEnum.DOG_WALKING:
        return t`Your dog gets a walk around your neighborhood. Perfect for busy days and dogs with extra energy to burn.`;
      case ServiceLandingEnum.DOGGY_DAY_CARE:
        return t`Your dog spends the day at your sitter’s home. Drop them off in the morning and pick up a happy pup in the evening.`;
      case ServiceLandingEnum.PET_SITTING:
        return t`Your sitter drops by your home to play with your pets, offer food, and give potty breaks or clean the litter box.`;
      case ServiceLandingEnum.DOG_TRAINING:
        return t`You and your dog work with a private trainer to build new skills and address behaviors. Meet your trainer in your home or neighborhood.`;
      default:
        return '';
    }
  };

  return {
    IconClass: (ICON_FOR_SERVICE_LANDING_ENUM[serviceLandingType] ??
      undefined) as React.ComponentType<{ fill?: string }>,
    title: getServiceLinkDisplayName(serviceLandingType),
    content: getServiceContent(serviceLandingType),
    color: colors.brand.active_brand,
    hasBorder: false,
    href,
    serviceLandingType,
  };
};

export const getAvailableServiceDescriptions = (
  servicesForCurrentRoute: BrandCountryServices | null,
  colors: RoverTheme['colors'],
  {
    shouldShowTrainingEntrypoints = false,
  }: {
    shouldShowTrainingEntrypoints?: boolean;
  } = {}
): ServiceDescription[] => {
  return NAVBAR_SERVICE_OPTIONS.map((serviceLandingType) =>
    getServiceDescriptionFromLandingEnum(serviceLandingType, servicesForCurrentRoute, colors, {
      shouldShowTrainingEntrypoints,
    })
  ).filter((service) => service !== null);
};
